import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { useAuth } from './AuthContext'; // Для получения user, если потребуется для запросов

const DataContext = createContext();

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const { user, loading: authLoading, isAuthenticated } = useAuth(); // Добавляем authLoading и isAuthenticated

  // Состояния для историй
  const [stories, setStories] = useState([]);
  const [areStoriesLoading, setAreStoriesLoading] = useState(true);
  const [storiesError, setStoriesError] = useState(null);
  const [storiesLoaded, setStoriesLoaded] = useState(false); // Флаг, что истории уже загружались

  // Состояния для контента
  const [content, setContent] = useState([]);
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [contentError, setContentError] = useState(null);
  const [contentLoaded, setContentLoaded] = useState(false); // Флаг, что "все" материалы (без фильтра) были успешно загружены хотя бы раз
  const [initialAllContentLoaded, setInitialAllContentLoaded] = useState(false); // Флаг для отслеживания самой первой загрузки "всех" материалов
  
  // Кэш контента по тегам для предотвращения мерцания
  const [contentCache, setContentCache] = useState({});
  const [currentTag, setCurrentTag] = useState('all'); // Текущий отображаемый тег

  // Состояния для тегов
  const [tags, setTags] = useState([]);
  const [areTagsLoading, setAreTagsLoading] = useState(true); // Изначально true, если грузим сразу
  const [tagsError, setTagsError] = useState(null);
  const [tagsLoaded, setTagsLoaded] = useState(false); // Флаг, что теги уже загружались

  // Флаг для отслеживания завершения самой первой загрузки всех данных для главной страницы
  const [isInitialHomeLoadComplete, setIsInitialHomeLoadComplete] = useState(false);

  // --- Загрузка историй ---
  const fetchStoriesData = useCallback(async (forceReload = false) => {
    if (storiesLoaded && !forceReload) {
      console.log('[DataContext] Stories already loaded and no forceReload, ensuring loading state is false.');
      setAreStoriesLoading(false);
      return;
    }
    console.log('[DataContext] Fetching stories...');
    setAreStoriesLoading(true);
    setStoriesError(null);
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const headers = telegramId ? { 'X-Telegram-ID': telegramId } : {};
      const timestamp = new Date().getTime();
      const response = await fetch(`/api/stories?_t=${timestamp}&sort=sortOrder`, {
        headers: { ...headers, 'Content-Type': 'application/json' },
        cache: 'no-cache',
      });
      if (!response.ok) throw new Error(`Error fetching stories: ${response.status}`);
      const data = await response.json();
      const formattedStories = (data && Array.isArray(data))
        ? data
            .filter(story => story.items && story.items.length > 0 && story.isActive !== false)
            .map(story => ({
              id: story._id,
              title: story.title || 'История',
              url: story.items[0].imageUrl || story.items[0].videoUrl || '',
              type: story.items[0].type || 'image',
              backgroundColor: story.backgroundColor || 'linear-gradient(to bottom, #f09433, #e6683c)',
              emoji: story.emoji || '😊',
              items: story.items.map(item => ({
                id: item._id,
                url: item.imageUrl || item.videoUrl || '',
                type: item.type || 'image',
                duration: item.type === 'image' ? 7000 : 0 // IMAGE_DURATION
              }))
            }))
        : [];
      setStories(formattedStories);
      setStoriesLoaded(true); // Устанавливаем флаг после успешной загрузки
    } catch (err) {
      console.error('[DataContext] Error in fetchStoriesData:', err);
      setStoriesError(err.message);
      setStories([]); // Сбрасываем истории при ошибке
    } finally {
      setAreStoriesLoading(false);
    }
  }, [user, storiesLoaded]); // Восстанавливаем storiesLoaded в зависимости

  // --- Загрузка контента ---
  // fetchContentData может принимать selectedTag для фильтрации
  const fetchContentData = useCallback(async (selectedTag = 'all', forceReload = false) => { // Убираем context
    // Проверяем кэш для предотвращения мерцания
    // const cacheKey = selectedTag; // Возвращаем простой cacheKey
    if (!forceReload && contentCache[selectedTag]) { // Используем selectedTag для ключа кэша
      console.log(`[DataContext] Using cached content for tag: ${selectedTag}`);
      setContent(contentCache[selectedTag]); // Используем selectedTag
      setCurrentTag(selectedTag);
      setIsContentLoading(false);
      setContentLoaded(selectedTag === 'all');
      return;
    }
    
    // Логика для contentLoaded:
    // - true: если последний успешный запрос был для 'all' (без фильтра по тегу).
    // Логика пропуска: если не принудительная перезагрузка, запрашиваются "все" теги,
    // и "все" теги уже были успешно загружены один раз (initialAllContentLoaded === true),
    // И текущий отображаемый контент УЖЕ является "всеми" (contentLoaded === true), то пропускаем.
    // Это предотвратит лишний запрос, если пользователь кликает "Все", когда "Все" уже отображается.
    if (!forceReload && selectedTag === 'all' && initialAllContentLoaded && contentLoaded && currentTag === 'all') { // Убираем проверку context
      console.log('[DataContext] Content (all) is already loaded (initial and current) and no forceReload. Skipping fetch.');
      setIsContentLoading(false);
      return;
    }
    
    console.log(`[DataContext] Fetching content (tag: ${selectedTag}, forceReload: ${forceReload}). InitialAllContentLoaded: ${initialAllContentLoaded}, CurrentContentLoaded: ${contentLoaded}`); // Убираем context из лога
    
    // Устанавливаем состояние загрузки только если нет текущего контента для предотвращения мерцания
    if (content.length === 0 || forceReload) {
      setIsContentLoading(true);
    }
    setContentError(null);
    setCurrentTag(selectedTag);
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const authHeaders = telegramId ? { 'X-Telegram-ID': telegramId } : {};
      let url = '/api/content';
      const params = new URLSearchParams();
      if (selectedTag && selectedTag !== 'all') {
        params.append('tag', selectedTag);
      }
      params.append('sortBy', 'sortOrder'); // Всегда добавляем сортировку
      // Убираем добавление giftContext
      // if (context === 'giftPurchase') {
      //   params.append('giftContext', 'true');
      // }

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      const response = await fetch(url, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      if (!response.ok) throw new Error(`Error fetching content: ${response.status}`);
      const data = await response.json();
      const contentItems = (data && data.content) ? data.content : (Array.isArray(data) ? data : []);
      
      // Обновляем кэш
      setContentCache(prev => ({
        ...prev,
        [selectedTag]: contentItems // Используем selectedTag как ключ
      }));
      
      setContent(contentItems); // Обновляем контент
      
      // Возвращаем оригинальную логику для contentLoaded
      if (selectedTag === 'all') {
        setContentLoaded(true); // Текущий контент теперь "все".
        if (!initialAllContentLoaded) {
          setInitialAllContentLoaded(true); // Первая загрузка "всех" завершена.
        }
      } else {
        setContentLoaded(false); // Текущий контент отфильтрован.
      }
    } catch (err) {
      console.error('[DataContext] Error in fetchContentData:', err);
      setContentError(err.message);
      setContent([]); 
      if (selectedTag === 'all') {
        setContentLoaded(false);
        setInitialAllContentLoaded(false); // Если ошибка при загрузке "всех", сбрасываем и этот флаг.
      }
      // Если ошибка при загрузке по тегу, contentLoaded уже должен быть false.
    } finally {
      setIsContentLoading(false);
    }
  }, [user, contentLoaded, initialAllContentLoaded, contentCache, content.length, currentTag]); // Зависимости useCallback

  // --- Загрузка тегов ---
  const fetchTagsData = useCallback(async (forceReload = false) => {
    if (tagsLoaded && !forceReload) {
        console.log('[DataContext] Tags already loaded and no forceReload, ensuring loading state is false.');
        setAreTagsLoading(false);
        return;
    }
    console.log('[DataContext] Fetching tags...');
    setAreTagsLoading(true); // Устанавливаем в true перед загрузкой
    setTagsError(null);
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const authHeaders = telegramId ? { 'X-Telegram-ID': telegramId } : {};
      const response = await fetch('/api/tags?sort=sortOrder', { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      if (!response.ok) throw new Error(`Error fetching tags: ${response.status}`);
      const data = await response.json();
      setTags(data || []);
      setTagsLoaded(true);
    } catch (err) {
      console.error('[DataContext] Error in fetchTagsData:', err);
      setTagsError(err.message);
      setTags([]);
    } finally {
      setAreTagsLoading(false);
    }
  }, [user, tagsLoaded]); // Восстанавливаем tagsLoaded в зависимости

  // Эффект для сброса состояния загрузки при смене пользователя или статуса аутентификации
  useEffect(() => {
    // Сбрасываем флаги загрузки, только если пользователь действительно изменился
    // или если authLoading перешло из true в false (т.е. аутентификация завершилась)
    // и пользователь аутентифицирован.
    console.log(`[DataContext] Auth state changed: authLoading=${authLoading}, isAuthenticated=${isAuthenticated}. User ID: ${user?.telegramId}`);
    
    // Сбрасываем и начинаем загрузку, если аутентификация завершена и пользователь есть (или сменился)
    if (!authLoading && user) { // Пользователь может быть гостем с telegramId=0, но он все равно "есть"
      console.log('[DataContext] Auth finished and user present. Resetting loaded flags and initiating data load.');
      setStoriesLoaded(false);
      setAreStoriesLoading(true);
      setContentLoaded(false);
      setInitialAllContentLoaded(false); // Сбрасываем и этот флаг
      setIsContentLoading(true);
      setContentCache({}); // Сбрасываем кэш при смене пользователя
      setCurrentTag('all');
      setTagsLoaded(false);
      setAreTagsLoading(true);
      setIsInitialHomeLoadComplete(false); // Сбрасываем флаг первоначальной загрузки
    } else if (!authLoading && !user) {
      // Если аутентификация завершена, но пользователя нет (например, после logout)
      console.log('[DataContext] Auth finished, no user. Clearing data and flags.');
      setStories([]); setStoriesLoaded(true); setAreStoriesLoading(false);
      setContent([]); setContentLoaded(true); setInitialAllContentLoaded(true); setIsContentLoading(false);
      setTags([]); setTagsLoaded(true); setAreTagsLoading(false);
      setIsInitialHomeLoadComplete(true); // Считаем, что для гостя "первоначальная загрузка" завершена (нет данных для загрузки)
    }
    // Если authLoading все еще true, ничего не делаем, ждем завершения.
  }, [user, authLoading, isAuthenticated]);

  // Эффект для отслеживания завершения первоначальной загрузки всех данных для главной
  useEffect(() => {
    // isInitialHomeLoadComplete устанавливается, когда stories, initial 'all' content, и tags загружены
    if (!isInitialHomeLoadComplete && storiesLoaded && initialAllContentLoaded && tagsLoaded) {
      console.log('[DataContext] Initial data load for home page is complete (stories, all content, tags).');
      setIsInitialHomeLoadComplete(true);
    }
  }, [storiesLoaded, initialAllContentLoaded, tagsLoaded, isInitialHomeLoadComplete]);


  // Эффекты для загрузки данных, если они еще не загружены и аутентификация завершена
  useEffect(() => {
    if (!authLoading && ((isAuthenticated && user) || !isAuthenticated) && !storiesLoaded) {
      console.log('[DataContext] Initial load trigger: Auth ready, stories not loaded. Fetching stories.');
      fetchStoriesData();
    }
  }, [authLoading, isAuthenticated, user, storiesLoaded, fetchStoriesData]); // Добавлен user

  useEffect(() => {
    // Загружаем 'all' контент только если:
    // 1. Аутентификация завершена
    // 2. И флаг initialAllContentLoaded еще не установлен (т.е. самая первая загрузка 'all' не была успешной)
    if (!authLoading && ((isAuthenticated && user) || !isAuthenticated) && !initialAllContentLoaded) {
      console.log('[DataContext] Initial load trigger: Auth ready, initial "all" content not loaded. Fetching content (all).');
      fetchContentData('all'); 
    }
  }, [authLoading, isAuthenticated, user, initialAllContentLoaded, fetchContentData]); // Добавлен user

  useEffect(() => {
    if (!authLoading && ((isAuthenticated && user) || !isAuthenticated) && !tagsLoaded) {
      console.log('[DataContext] Initial load trigger: Auth ready, tags not loaded. Fetching tags.');
      fetchTagsData();
    } 
    // Блок else if был удален в предыдущих правках, так как условие в if уже покрывает оба случая
    // else if (!authLoading && !isAuthenticated && !tagsLoaded) { 
    //   console.log('[DataContext] Auth ready (not authenticated), tags not loaded. Attempting to fetch tags (e.g., public).');
    //   fetchTagsData();
    // }
  }, [authLoading, isAuthenticated, user, tagsLoaded, fetchTagsData]); // Добавлен user

  const value = {
    stories, areStoriesLoading, storiesError, fetchStoriesData,
    content, isContentLoading, contentError, fetchContentData,
    tags, areTagsLoading, tagsError, fetchTagsData,
    // Добавляем флаги для более гранулярного контроля, если нужно
    storiesLoaded, contentLoaded, tagsLoaded,
    isInitialHomeLoadComplete, // Экспортируем новый флаг
    // Функции для сброса состояния загруженности (для pull-to-refresh и т.п.)
    refreshAllData: () => {
        console.log('[DataContext] Refreshing all data...');
        setStoriesLoaded(false);
        setContentLoaded(false); // Сбрасываем, т.к. текущий content (возможно отфильтрованный) не актуален
        setInitialAllContentLoaded(false); // Сбрасываем флаг первоначальной загрузки "всех"
        setTagsLoaded(false);
        setContentCache({}); // Очищаем кэш при обновлении всех данных
        setCurrentTag('all');
        setIsInitialHomeLoadComplete(false); 
        
        setAreStoriesLoading(true);
        setIsContentLoading(true); // Устанавливаем, т.к. будем грузить 'all'
        setAreTagsLoading(true);

        fetchStoriesData(true);
        fetchContentData('all', true); 
        fetchTagsData(true);
    }
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
